import type { Component } from 'solid-js'
import { createEffect, createMemo, createResource, createSignal, on, Show } from 'solid-js'
import { Trans } from '@mbarzda/solid-i18next'
import {
  getDateRangesForSimplePeriod,
  getDateRangesForCustomPeriod,
  formatDateToApiString,
  type SimplePeriodType,
  type CompareMode
} from '../../shared/helpers/date.functions'
import { useCorisEngagement } from '../../shared/providers/coris/coris-engagement.provider'
import { useUser } from '../../shared/providers/user-provider'
import { usePeriods, type EngagementPeriodState } from '../../shared/providers/period.provider'
import type { UserSegment } from '../../features/engagement/domain/models/sections'
import PeriodSelector from '../shared/components/PeriodSelector'
import SummaryCard from '../shared/components/SummaryCard'
import EmptyState from '../shared/components/EmptyState'
import { sticky } from '../../shared/directives/sticky'
import { SkeletonSummaryCard, SkeletonChart, SkeletonDonutChart } from '../shared/components/Skeleton'
import SectionsTotalsChart from './sections/SectionsTotalsChart'
import SectionsStackedChart from './sections/SectionsStackedChart'
import SectionsTimeDonut from './sections/SectionsTimeDonut'
import SectionsVisitsBars from './sections/SectionsVisitsBars'
import SectionsPerUserBlock from './sections/SectionsPerUserBlock'
import SectionsTable from './sections/SectionsTable'
import UserSegmentToggle from './sections/UserSegmentToggle'
import ToggleGroup from './sections/ToggleGroup'
import { formatDurationHours } from './sections/section-display'
import { SECTIONS_METRIC_OPTIONS, type SectionsMetricKey } from './sections/SectionsStackedChart.vm'

// Prevent tree-shaking of directive
sticky

const resolveDateRanges = (period: EngagementPeriodState) => {
  const dateRanges =
    period.selectedPeriod === 'custom' && period.customDateRange
      ? getDateRangesForCustomPeriod(period.customDateRange.from, period.customDateRange.to)
      : getDateRangesForSimplePeriod(period.selectedPeriod)

  let fromPrevious = dateRanges.fromPrevious
  let toPrevious = dateRanges.toPrevious

  if (period.compareMode === 'custom_range' && period.compareCustomRange) {
    fromPrevious = formatDateToApiString(period.compareCustomRange.from)
    toPrevious = formatDateToApiString(period.compareCustomRange.to)
  }

  return { fromDate: dateRanges.fromDate, toDate: dateRanges.toDate, fromPrevious, toPrevious }
}

const Sections: Component = () => {
  const { engagementPeriod, setEngagementPeriod } = usePeriods()
  const [segment, setSegment] = createSignal<UserSegment>('all')
  const [metric, setMetric] = createSignal<SectionsMetricKey>('seconds')
  const [isStuck, setIsStuck] = createSignal(false)

  const { getSectionsSummary, getSectionsComparison } = useCorisEngagement()
  const { organizationSlug } = useUser()

  // Segment keys are per-organization tiers, so the selection only makes sense within one organization
  createEffect(on(organizationSlug, () => setSegment('all'), { defer: true }))

  const [summaryData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      return { ...engagementPeriod(), userSegment: segment(), organization }
    },
    async (params) => {
      if (!params) return null

      const dateRanges = resolveDateRanges(params)

      return await getSectionsSummary.execute({
        ...dateRanges,
        userSegment: params.userSegment,
        organization: params.organization
      })
    }
  )

  const segments = createMemo(() => summaryData()?.segments ?? [])
  // Segmentation (toggle + per-user comparison) only makes sense with at least two segments
  const hasSegments = createMemo(() => segments().length >= 2)

  // 'other' (auth/onboarding transit, paywall, unmapped routes) is intentionally
  // hidden from the whole page; KPI totals still include it.
  const visibleSections = createMemo(() => (summaryData()?.sections ?? []).filter((s) => s.section !== 'other'))

  // Comparison source holds only the params the /comparison request depends on (it ignores the
  // user segment and the compare dates server-side), so summary refetches don't retrigger it.
  // Custom equality keeps the key stable when the source re-evaluates to the same values.
  const comparisonSource = createMemo(
    () => {
      const organization = organizationSlug()
      if (!organization || !hasSegments()) return null
      const { fromDate, toDate } = resolveDateRanges(engagementPeriod())
      return { organization, fromDate, toDate }
    },
    undefined,
    {
      equals: (a, b) => a?.organization === b?.organization && a?.fromDate === b?.fromDate && a?.toDate === b?.toDate
    }
  )

  // Fetched only when the artist has at least two segments; a comparison failure must not blank the page
  const [comparisonData] = createResource(comparisonSource, async (params) => {
    if (!params) return null

    return await getSectionsComparison.execute(params)
  })

  // createResource keeps the previously resolved value during a refetch, so the presence
  // of data can't gate the skeletons: switching tier or period would re-render the resolved
  // charts against stale numbers. Gate on the resource state instead.
  const isLoadingSummary = createMemo(() => summaryData.state === 'unresolved' || summaryData.loading)
  const isLoadingComparison = createMemo(() => comparisonData.state === 'unresolved' || comparisonData.loading)

  const isEmpty = createMemo(() => {
    const data = summaryData()
    return (
      data &&
      data.totals.visits.current === 0 &&
      data.totals.uniqueVisitors.current === 0 &&
      data.totals.seconds.current === 0 &&
      data.totals.interactions.current === 0
    )
  })

  const handlePeriodChange = (period: SimplePeriodType) => {
    setEngagementPeriod({ selectedPeriod: period })
  }

  const handleCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ customDateRange: { from, to } })
  }

  const handleCompareModeChange = (mode: CompareMode) => {
    setEngagementPeriod({ compareMode: mode })
  }

  const handleCompareCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ compareCustomRange: { from, to } })
  }

  return (
    <div class="flex flex-col gap-4 px-6">
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 py-6 transition-colors duration-200 -mx-6 px-6"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent border-b border-transparent': !isStuck()
        }}
      >
        <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div class="flex flex-col gap-1.5">
            <PeriodSelector
              selectedPeriod={engagementPeriod().selectedPeriod}
              onPeriodChange={handlePeriodChange}
              customDateRange={engagementPeriod().customDateRange}
              onCustomRangeChange={handleCustomRangeChange}
              compareMode={engagementPeriod().compareMode}
              onCompareModeChange={handleCompareModeChange}
              compareCustomRange={engagementPeriod().compareCustomRange}
              onCompareCustomRangeChange={handleCompareCustomRangeChange}
            />
            {/* One page-level note instead of a per-metric marker: every time figure on
                this page is derived from web pageview durations. */}
            <p class="text-xs text-gray-400">
              <Trans key="ls_sections_time_web_note" />
            </p>
          </div>
          <Show when={hasSegments()}>
            <UserSegmentToggle segments={segments()} value={segment()} onChange={setSegment} />
          </Show>
        </div>
      </div>

      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-8">
        <Show when={!isLoadingSummary() && summaryData() && isEmpty()}>
          <EmptyState />
        </Show>

        <Show when={isLoadingSummary() || !isEmpty()}>
          <Show
            when={!isLoadingSummary() && summaryData()?.totals}
            fallback={
              <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
              </div>
            }
          >
            <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <SummaryCard
                title="ls_sections_visits"
                value={summaryData()!.totals.visits.current}
                change={summaryData()!.totals.visits.diff}
              />
              <SummaryCard
                title="ls_sections_unique_visitors"
                value={summaryData()!.totals.uniqueVisitors.current}
                change={summaryData()!.totals.uniqueVisitors.diff}
              />
              <SummaryCard
                title="ls_sections_time"
                value={formatDurationHours(summaryData()!.totals.seconds.current)}
                change={summaryData()!.totals.seconds.diff}
              />
              <SummaryCard
                title="ls_sections_interactions"
                value={summaryData()!.totals.interactions.current}
                change={summaryData()!.totals.interactions.diff}
              />
            </div>
          </Show>

          {/* A cross-tier comparison contradicts a single-tier filter, so it only
              shows on 'All'. */}
          <Show when={hasSegments() && segment() === 'all' && !comparisonData.error}>
            <Show
              when={!isLoadingComparison() && comparisonData()}
              fallback={<SkeletonChart legendItems={segments().length} />}
            >
              <SectionsPerUserBlock comparison={comparisonData()!} />
            </Show>
          </Show>

          <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
            <Show
              when={!isLoadingSummary() && summaryData()?.sections}
              fallback={<SkeletonDonutChart legendItems={5} />}
            >
              <SectionsTimeDonut sections={visibleSections()} />
            </Show>
            <Show when={!isLoadingSummary() && summaryData()?.sections} fallback={<SkeletonChart legendItems={5} />}>
              <SectionsVisitsBars sections={visibleSections()} />
            </Show>
          </div>

          <Show when={!isLoadingSummary() && summaryData()?.periods} fallback={<SkeletonChart legendItems={3} />}>
            <SectionsTotalsChart periods={summaryData()!.periods} />
          </Show>

          <Show when={!isLoadingSummary() && summaryData()?.sections} fallback={<SkeletonChart legendItems={8} />}>
            <div class="flex flex-col gap-3">
              <div class="flex justify-end">
                <ToggleGroup options={SECTIONS_METRIC_OPTIONS} value={metric()} onChange={setMetric} />
              </div>
              <SectionsStackedChart sections={visibleSections()} metric={metric()} />
            </div>
          </Show>

          <SectionsTable sections={visibleSections()} isLoading={isLoadingSummary()} />
        </Show>
      </div>
    </div>
  )
}

export default Sections
